/**
 * BoardChrome — the frame and floating controls the three alert boards share.
 *
 * Each board is one rounded card holding its canvas edge to edge, with its controls FLOATING
 * over it as small white cards rather than stacked in toolbar rows above it: the canvas is the
 * thing an operator watches, and every toolbar row was a strip of it they could not see.
 *
 *   top-left      Filters pill, then an identity chip (which board, how big)
 *   top-centre    whatever the page slots in — the view toggle while full screen
 *   top-right     a joined button group: zoom out / in, Fit, full screen
 *   bottom-left   the legend (BoardLegend positions itself)
 *
 * The filter panel opens IN the card at its top-left, in flow on wide screens — the canvas
 * beside it narrows and the honeycomb re-lays out into the width that is left — and over the
 * canvas on narrow ones, where pushing would leave neither readable. Its height is its content.
 *
 * One file so the three boards cannot drift: the same pill, chip and group, in the same places,
 * whichever view is on screen.
 */
import { forwardRef, type ComponentType, type CSSProperties, type ReactNode } from 'react';
import { AdjustmentsHorizontalIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { FilterCheck, FilterSection } from '../layout/FilterColumn';
import { BOARD_SEVERITIES, type BoardFilters } from '../../hooks/useBoardFilters';
import type { FilterGroup } from './HexFilterBar';

/** The floating-card surface every control over the canvas wears. */
export const BOARD_FLOAT = 'rounded-xl border border-border bg-card shadow-card';

/** "12 cams" — what the Filters pill reports. */
export const camsLabel = (n: number) => `${n} cam${n === 1 ? '' : 's'}`;

// A faint dot grid on a slightly-off-card ground, so the white floating controls read as
// sitting ON the board. Inline because the theme colours are bare var()s and Tailwind's
// opacity modifiers on them compile to nothing.
const CARD_STYLE: CSSProperties = {
  backgroundColor: 'color-mix(in srgb, var(--muted) 22%, var(--card))',
  backgroundImage:
    'radial-gradient(color-mix(in srgb, var(--muted-foreground) 22%, transparent) 1px, transparent 1.3px)',
  backgroundSize: '18px 18px',
};

interface BoardCardProps {
  /** The open filter panel, or nothing. */
  panel?: ReactNode;
  /** Size classes (the boards give it a fixed height on phones, flex-1 on wide screens). */
  className?: string;
  /** The canvas area — its root is what the ref lands on, so boards measure IT, not the card. */
  children: ReactNode;
}

/** The board's card. The forwarded ref is the CANVAS area (beside the panel), which is what a
 *  board's ResizeObserver must watch — opening the panel narrows it. */
export const BoardCard = forwardRef<HTMLDivElement, BoardCardProps>(function BoardCard(
  { panel, className = '', children },
  ref,
) {
  return (
    <div className={`relative flex overflow-hidden rounded-xl border border-border shadow-card ${className}`} style={CARD_STYLE}>
      {panel && (
        // Overlay below lg (pointer-events pass through to the canvas around the panel); a
        // column in flow from lg, so the canvas beside it shrinks.
        <div className="pointer-events-none absolute inset-y-3 left-3 z-[900] flex lg:pointer-events-auto lg:static lg:z-auto lg:flex-none lg:py-3 lg:pl-3">
          <div className="pointer-events-auto flex max-h-full self-start">{panel}</div>
        </div>
      )}
      <div ref={ref} className="relative min-w-0 flex-1">
        {children}
      </div>
    </div>
  );
});

/** The row of floating controls along the canvas's top edge. Clicks between the cards fall
 *  through to the canvas. */
export function BoardTopBar({
  left,
  center,
  right,
  className = 'z-40',
  insetClass = 'inset-x-3',
}: {
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  /** z-index — the geographic map's own layers sit in the hundreds. */
  className?: string;
  /** Horizontal inset — the honeycomb keeps clear of its scrollbar on the right. */
  insetClass?: string;
}) {
  return (
    <div className={`pointer-events-none absolute top-3 flex items-start gap-2 ${insetClass} ${className}`}>
      <div className="flex min-w-0 flex-1 flex-wrap items-start gap-2 [&>*]:pointer-events-auto">{left}</div>
      {center && <div className="pointer-events-auto flex-none">{center}</div>}
      <div className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-2 [&>*]:pointer-events-auto">{right}</div>
    </div>
  );
}

/** Board icon + bold name + one muted line of counts or hint. */
export function BoardIdentityChip({
  icon: Icon,
  title,
  detail,
  titleText,
}: {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  detail?: ReactNode;
  /** Plain-text tooltip, for when either line truncates. */
  titleText?: string;
}) {
  return (
    <div className={`${BOARD_FLOAT} flex h-10 min-w-0 max-w-[28rem] items-center gap-2.5 px-3`} title={titleText}>
      <Icon className="h-4 w-4 flex-none text-brand" />
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-[13px] font-bold text-foreground">{title}</span>
        {detail && <span className="truncate text-[11px] text-muted-foreground">{detail}</span>}
      </div>
    </div>
  );
}

/** A joined group of tool buttons — zoom, Fit, full screen. */
export function BoardToolGroup({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`${BOARD_FLOAT} flex h-10 items-stretch divide-x divide-border overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

export function BoardToolButton({
  label,
  title,
  onClick,
  disabled,
  pressed,
  children,
}: {
  /** Accessible name; also the tooltip unless `title` says more. */
  label: string;
  title?: string;
  onClick: () => void;
  disabled?: boolean;
  /** A toggle that is on (the honeycomb's "All"). */
  pressed?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      aria-pressed={pressed}
      onClick={onClick}
      disabled={disabled}
      className={`flex min-w-[2.5rem] items-center justify-center gap-1.5 px-2.5 text-[13px] font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 ${
        pressed ? 'bg-muted font-semibold text-brand' : 'text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

/** Four corner brackets, out or in — the full-screen glyph, distinct from Fit's arrows. */
function CornersIcon({ inward = false, className = 'h-4 w-4' }: { inward?: boolean; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d={inward ? 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5' : 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5'} />
    </svg>
  );
}

/** The group's last button. Always drawn when the page offers full screen — it is also the way
 *  OUT, so it must not depend on the board having anything to zoom. */
export function BoardFullscreenButton({
  fullscreen,
  onToggle,
  disabled,
}: {
  fullscreen: boolean;
  onToggle: () => void;
  /** Mid-edit: flipping the frame under unsaved pins is refused, as before. */
  disabled?: boolean;
}) {
  return (
    <BoardToolButton
      label={fullscreen ? 'Exit full screen' : 'Full screen'}
      title={disabled ? 'Save or cancel the layout first' : fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
      onClick={onToggle}
      disabled={disabled}
      pressed={fullscreen}
    >
      <CornersIcon inward={fullscreen} />
    </BoardToolButton>
  );
}

/** The pill's inside, shared by the folded pill and the open panel's header. */
function FilterHead({ countLabel, activeCount, open }: { countLabel: string; activeCount: number; open: boolean }) {
  const Chevron = open ? ChevronUpIcon : ChevronDownIcon;
  return (
    <>
      <AdjustmentsHorizontalIcon className="h-4 w-4 flex-none text-muted-foreground" />
      <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-foreground">Filters</span>
      {/* Folded, the board's dimming needs a visible cause — this badge is it. */}
      {activeCount > 0 && (
        <span className="flex-none rounded-full bg-brand px-1.5 text-[10px] font-bold leading-4 text-white tabular-nums">
          {activeCount}
        </span>
      )}
      <span className="ml-auto pl-1 text-[12px] tabular-nums text-muted-foreground">{countLabel}</span>
      <Chevron className="h-3.5 w-3.5 flex-none text-muted-foreground" />
    </>
  );
}

/** The folded state: one pill that opens the panel. */
export function BoardFilterPill({
  onToggle,
  countLabel,
  activeCount,
}: {
  onToggle: () => void;
  countLabel: string;
  activeCount: number;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={false}
      title="Show the board's filters"
      className={`${BOARD_FLOAT} flex h-10 items-center gap-2.5 px-3.5 transition-colors hover:bg-muted`}
    >
      <FilterHead countLabel={countLabel} activeCount={activeCount} open={false} />
    </button>
  );
}

const baseOf = (g: FilterGroup) => g.defaultValue ?? g.chips[0]?.key;

/**
 * The open state: the pill grown into a panel. Alert type and site are single-choice radio
 * lists with counts; severity is multi-select (none = all); "only open" is a checkbox. The
 * footer says what the choices DID — a dimmed camera and a quiet one look alike — and Clear
 * resets every section, the site included.
 */
export function BoardFilterPanel({
  filters,
  onCollapse,
  countLabel,
  site,
  matchCount,
  totalCount,
}: {
  filters: BoardFilters;
  onCollapse: () => void;
  countLabel: string;
  /** The honeycomb's Site group on a multi-site board (its effect is 'hide'). */
  site?: FilterGroup | null;
  matchCount: number;
  totalCount: number;
}) {
  // Only a real choice is drawn — one site is nothing to pick between.
  const siteGroup = site && site.chips.length > 1 ? site : null;
  const siteOn = !!siteGroup && siteGroup.value !== baseOf(siteGroup);
  const applied = filters.activeCount + (siteOn ? 1 : 0);
  const fate = siteOn && filters.activeCount > 0 ? 'hidden or dimmed' : siteOn ? 'hidden' : 'dimmed';
  const clearAll = () => {
    filters.clear();
    if (siteGroup && siteOn) siteGroup.onChange(baseOf(siteGroup) as string);
  };
  const typeGroup = filters.alertType.group;

  return (
    <div className={`${BOARD_FLOAT} flex max-h-full w-[250px] flex-col overflow-hidden`} role="group" aria-label="Filter the board">
      <button
        type="button"
        onClick={onCollapse}
        aria-expanded
        title="Hide the filters"
        className="flex h-10 flex-none items-center gap-2.5 border-b-2 border-brand px-3.5 transition-colors hover:bg-muted"
      >
        <FilterHead countLabel={countLabel} activeCount={applied} open />
      </button>

      <div className="min-h-0 space-y-4 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
        {siteGroup && (
          <FilterSection title={siteGroup.label}>
            <div role="radiogroup" aria-label="Filter by site" className="-mx-1 space-y-0.5">
              {siteGroup.chips.map((chip) => (
                <FilterCheck
                  key={chip.key}
                  radio
                  checked={chip.key === siteGroup.value}
                  onClick={() => siteGroup.onChange(chip.key)}
                  label={<span title={chip.title || chip.label}>{chip.label}</span>}
                  count={chip.count ?? null}
                />
              ))}
            </div>
          </FilterSection>
        )}

        <FilterSection title="Alert type">
          <div role="radiogroup" aria-label="Filter by alert type" className="-mx-1 space-y-0.5">
            {typeGroup.chips.map((chip) => (
              <FilterCheck
                key={chip.key}
                radio
                checked={chip.key === typeGroup.value}
                onClick={() => typeGroup.onChange(chip.key)}
                label={<span title={chip.title || chip.label}>{chip.label}</span>}
                count={chip.count ?? null}
              />
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Severity">
          <div role="group" aria-label="Filter by severity" className="flex flex-wrap gap-1.5">
            {BOARD_SEVERITIES.map((s) => {
              const on = filters.severities.has(s.key);
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => filters.toggleSeverity(s.key)}
                  title={`Only cameras with an open ${s.label.toLowerCase()} alert`}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                    on ? 'border-brand bg-brand-light font-semibold text-brand' : 'border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <span aria-hidden className={`h-2 w-2 flex-none rounded-full ${s.dot}`} />
                  {s.label}
                </button>
              );
            })}
          </div>
        </FilterSection>

        <div className="-mx-1">
          <FilterCheck
            checked={filters.onlyOpen}
            onClick={() => filters.setOnlyOpen(!filters.onlyOpen)}
            label="Only cameras with open alerts"
          />
        </div>

        {applied > 0 && (
          <div className="flex items-start justify-between gap-2 border-t border-border pt-2.5">
            <p className="text-[11px] leading-snug text-muted-foreground" aria-live="polite">
              <span className="font-semibold text-foreground tabular-nums">{matchCount}</span> of{' '}
              <span className="tabular-nums">{totalCount}</span> cameras match — the rest are {fate}
            </p>
            <button
              type="button"
              onClick={clearAll}
              className="flex-none text-[12px] font-medium text-brand underline-offset-2 hover:underline"
            >
              Clear
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** What a board reports up for the page's title-bar pill. */
export interface BoardStats {
  /** Open alerts on the board. */
  open: number;
  /** Cameras in the panel's live-alert section. */
  live: number;
}
